import PropTypes from 'prop-types';
import {useCallback, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Route} from 'react-router-dom';
import {Message, Container, Dimmer, Loader} from 'semantic-ui-react';

import {isAuthenticated, isSecondary, isService} from 'state/selectors';

import styles from './ProtectedRoute.module.scss';

function InvalidAccountTypeError() {
  return (
    <Container text className={styles.SecondaryAccountUnauthorizedError}>
      <Message negative>
        <Message.Header>Site creation is not allowed</Message.Header>
        <p>
          You are logged in with either a CERN secondary or service account thus you can't own CERN
          websites. Only CERN primary can create new sites. More information on sites ownership is
          available{' '}
          <a
            href="https://webeos.docs.cern.ch/create_site/#site-owner"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          .
        </p>
      </Message>
    </Container>
  );
}
// Always use ProtectedRoute for all components which require the user to be logged in
const ProtectedRoute = ({
  component: Comp,
  path,
  location,
  allowSecondaryAccount,
  allowServiceAccount,
  ...rest
}) => {
  const isUserAuthenticated = useSelector(isAuthenticated);
  const isSecondaryAccount = useSelector(isSecondary);
  const isServiceAccount = useSelector(isService);
  const dispatch = useDispatch();

  // 1. Whenever navigating to this route store the path as a key in the localStorage
  // 2. The component is protected with SSO: if the user is not authenticated, redirect to login
  useEffect(() => {
    if (!isUserAuthenticated) {
      localStorage.setItem('location', window.location.href.replace(window.location.origin, ''));
      window.location.replace('/login');
    }
  }, [dispatch, isUserAuthenticated]);

  const renderComponent = useCallback(
    props => {
      if (!isUserAuthenticated) {
        return (
          <Dimmer className={styles.Dimmer} active>
            <Loader>Logging in</Loader>
          </Dimmer>
        );
      } else if (
        (!allowSecondaryAccount && isSecondaryAccount) ||
        (!allowServiceAccount && isServiceAccount)
      ) {
        return <InvalidAccountTypeError />;
      } else {
        return <Comp {...props} key={Date.now()} />;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isUserAuthenticated,
      allowSecondaryAccount,
      isSecondaryAccount,
      allowServiceAccount,
      isServiceAccount,
      Comp,
    ]
  );

  return (
    <Route
      {...rest}
      path={path}
      location={location}
      allowSecondaryAccount={allowSecondaryAccount}
      allowServiceAccount={allowServiceAccount}
      render={renderComponent}
    />
  );
};

ProtectedRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  path: PropTypes.string.isRequired,
  location: PropTypes.object,
  allowSecondaryAccount: PropTypes.bool.isRequired,
  allowServiceAccount: PropTypes.bool.isRequired,
};

ProtectedRoute.defaultProps = {
  allowSecondaryAccount: true,
  allowServiceAccount: true,
};

export default ProtectedRoute;
